import { apiFetchV2 } from '@m/utils/http';

import type { EvaluationContext } from '@m/services/experimentation/EvaluationContext';

export type { EvaluationContext };

export interface FlagOptions {
  enterpriseId?: number | null;
  siteId?: number | null;
  siteUuid?: string | null;
  userId?: number | null;
}

export interface FeatureGateResponse {
  enabled: boolean;
  name: string;
}

export const DISABLED_FEATURE_GATE_RESPONSE = (name: string): FeatureGateResponse => ({
  name,
  enabled: false,
});

export interface SetFeatureFlagSettingOptions {
  enabled?: boolean;
  enterprises?: number[];
  sites?: number[];
  siteUuids?: string[];
}

/** Flag with full state returned by getAllFeatureFlags({ includeState: true }) */
export type FeatureFlagWithState = { name: string } & Partial<SetFeatureFlagSettingOptions>;

export const FeatureFlagService = {
  archiveFeatureFlag: (name: string) =>
    apiFetchV2<undefined>(`/admin/feature/${name}`, { method: 'DELETE' }),
  createFeatureFlag: (name: string, { enabled, sites }: SetFeatureFlagSettingOptions = {}) =>
    apiFetchV2<SetFeatureFlagSettingOptions>(`/admin/feature/${name}`, {
      method: 'POST',
      body: sites ? { sites } : { enabled },
    }),
  evaluateFeatureGate: (featureGateName: string, evaluationContext?: EvaluationContext) =>
    apiFetchV2<FeatureGateResponse>('/feature-gate', {
      method: 'POST',
      body: { featureGateName, evaluationContext },
    }),
  getAdminFeatureFlagSetting: (name: string, options?: FlagOptions) =>
    apiFetchV2<SetFeatureFlagSettingOptions>(`/admin/feature/${name}`, {
      params: {
        siteId: options?.siteId,
        enterpriseId: options?.enterpriseId,
      },
    }),
  getAllFeatureFlags: ({ includeState = false }: { includeState?: boolean } = {}) =>
    apiFetchV2<{ flags: string[] } | { flags: FeatureFlagWithState[] }>('/admin/feature', {
      params: { includeState },
    }),
  getCustomerFeatureFlag: (name: string, options?: FlagOptions) =>
    apiFetchV2<boolean>(`/customer/feature/${name}`, {
      params: {
        siteId: options?.siteId,
        enterpriseId: options?.enterpriseId,
        siteUuid: options?.siteUuid,
        userId: options?.userId,
      },
    }),
  getSpecialistFeatureFlag: (name: string, options?: FlagOptions) =>
    apiFetchV2<boolean>(`/specialist/feature/${name}`, {
      params: {
        siteId: options?.siteId,
        enterpriseId: options?.enterpriseId,
      },
    }),
  setFeatureFlagSetting: (
    name: string,
    { enabled, sites, siteUuids, enterprises }: SetFeatureFlagSettingOptions,
  ) => {
    // API requires exactly one of: enabled, sites, enterprises, siteUuids, users (no mixing; do not send enabled with sites/enterprises)
    let body: Record<string, unknown>;
    if (enterprises && enterprises.length > 0) {
      body = { enterprises };
    } else if (sites && sites.length > 0) {
      body = { sites };
      if (siteUuids && siteUuids.length > 0) {
        body.siteUuids = siteUuids;
      }
    } else {
      body = { enabled: enabled ?? false };
    }

    return apiFetchV2<SetFeatureFlagSettingOptions>(`/admin/feature/${name}`, {
      method: 'POST',
      body,
    });
  },
};
